<script setup lang="ts">
import { ref, onMounted, watch, computed } from "vue";
import ABTestItem from "@/modules/testing/components/ABTestItem.vue";
import { useApiClient } from "@/api/client";
import { toastApiResult } from "@/toaster";
import useDialog from "@/ui/composables/useDialog";
import Modal from "@/ui/components/modal.vue";
import ModalEditABTest from "@/modules/testing/components/modal/ModalEditABTestItem.vue";

// Types
import ABTestDTO = App.Data.ABTest.ABTestDTO;
import ABTestPostDTO = App.Data.ABTest.ABTestPostDTO;
import ABTestPatchDTO = App.Data.ABTest.ABTestPatchDTO;
import ABTestVariantDTO = App.Data.ABTest.ABTestVariantDTO;
import MainBreadcrumbs from "@/ui/components/header/MainBreadcrumbs.vue";

interface TableOptions {
	page: number;
	itemsPerPage: number;
	sortBy: Array<{
		key: string;
		order: "asc" | "desc";
	}>;
}

type ModalMode = "create" | "edit";

// Services
const dialog = useDialog();
const api = useApiClient("testing");

// State Management
const tests = ref<ABTestDTO[]>([]);
const totalTests = ref(0);
const loading = ref(false);
const searchQuery = ref("");
const itemsPerPage = ref(10);
const isValidTest = ref(false);

// UI state
const showTestDetailsModal = ref(false);
const showEditModal = ref(false);
const selectedTest = ref<ABTestDTO | null>(null);
const editMode = ref<ModalMode>("create");
const editableTest = ref<ABTestDTO | null>(null);
const lastTableOptions = ref<TableOptions>({
	page: 1,
	itemsPerPage: itemsPerPage.value,
	sortBy: [],
});

// Table configuration
const tableHeaders = [
	{ title: "Name", key: "name", sortable: true },
	{ title: "Description", key: "description", sortable: false },
	{ title: "Variants", key: "variants.length", sortable: false },
	{ title: "Actions", key: "actions", sortable: false, align: "end" },
];

const breadcrumbs = [
	{
		title: "Tests",
		disabled: true,
		to: "/tests",
	},
];

/**
 * Fetches AB tests from the API with filtering, pagination, and sorting
 */
async function loadTests(options: TableOptions): Promise<void> {
	loading.value = true;
	lastTableOptions.value = options;

	try {
		// Construct sort parameter if sortBy is provided
		const sortParam = options.sortBy.length
			? `${options.sortBy[0].order === "desc" ? "-" : ""}${
					options.sortBy[0].key
			  }`
			: undefined;

		const params = {
			page: options.page,
			per_page: options.itemsPerPage,
			sort: sortParam,
			"filter[name]": searchQuery.value || undefined,
		};

		const result = await api.getABTests(params);

		if (result.isOk()) {
			tests.value = result.value.data;
			totalTests.value = result.value.total;
		} else {
			console.error("Error fetching tests:", result.error);
		}
	} catch (error) {
		console.error("Error fetching tests:", error);
	} finally {
		loading.value = false;
	}
}

function refreshData(): void {
	loadTests(lastTableOptions.value);
}

function handleSearch(): void {
	const options = {
		...lastTableOptions.value,
		page: 1, // Reset to first page on new search
	};
	loadTests(options);
}

// CRUD Operations
async function createTest(testData: ABTestPostDTO): Promise<void> {
	try {
		const result = await api.createABTest(testData);
		await toastApiResult(Promise.resolve(result));

		if (result.isOk()) {
			refreshData();
		}
	} catch (error) {
		console.error("Error creating test:", error);
	}
}

async function updateTest(testData: ABTestPatchDTO): Promise<void> {
	if (!selectedTest.value) return;

	try {
		loading.value = true;
		const result = await api.updateABTest(selectedTest.value.uuid, testData);
		await toastApiResult(Promise.resolve(result));

		if (result.isOk()) {
			refreshData();
			showTestDetailsModal.value = false;
		}
	} catch (error) {
		console.error("Error updating test:", error);
	}

	loading.value = false;
}

function saveTestChanges(): void {
	if (!editableTest.value || !selectedTest.value) return;

	const patchData: ABTestPatchDTO = {
		name: editableTest.value.name,
		description: editableTest.value.description,
		variants: editableTest.value.variants,
	};

	updateTest(patchData);
}

async function deleteTest(test: ABTestDTO): Promise<void> {
	const confirmed = await dialog.confirm({
		title: "Delete AB Test",
		message: `Are you sure you want to delete "${test.name}"? This action cannot be undone.`,
		isDelete: true,
	});

	if (!confirmed) return;

	try {
		const result = await api.deleteABTest(test.uuid);
		await toastApiResult(Promise.resolve(result));

		if (result.isOk()) {
			refreshData();
		}
	} catch (error) {
		console.error("Error deleting test:", error);
	}
}

// UI Handlers
function openEditModal(
	mode: ModalMode = "create",
	target: ABTestDTO | null = null
): void {
	selectedTest.value = target;
	editMode.value = mode;
	showEditModal.value = true;
}

function viewTestDetails(test: ABTestDTO): void {
	selectedTest.value = test;
	editableTest.value = cloneTest(test);
	showTestDetailsModal.value = true;
}

function closeTestDetails(): void {
	showTestDetailsModal.value = false;
	selectedTest.value = null;
	editableTest.value = null;
}

function cloneTest(test: ABTestDTO): ABTestDTO {
	return {
		uuid: test.uuid,
		name: test.name,
		description: test.description,
		is_locked: test.is_locked,
		variants: test.variants.map(
			(variant): ABTestVariantDTO => ({
				uuid: variant.uuid,
				name: variant.name,
				percentage: variant.percentage,
				is_control: variant.is_control,
				is_locked: variant.is_locked,
			})
		),
	};
}

// Reset editable test when modal visibility changes
watch(showTestDetailsModal, (isVisible) => {
	if (!isVisible) {
		editableTest.value = null;
	} else if (selectedTest.value && !editableTest.value) {
		editableTest.value = cloneTest(selectedTest.value);
	}
});

// Initialize data on component mount
onMounted(() => {
	loadTests({
		page: 1,
		itemsPerPage: itemsPerPage.value,
		sortBy: [],
	});
});
</script>

<template>
	<main-breadcrumbs :breadcrumbs="breadcrumbs" />
	<!-- Data Table Section -->
	<section>
		<v-row>
			<v-col cols="12">
				<v-card>
					<div class="px-5 pt-5 pb-3">
						<div class="d-flex flex-row ga-3">
							<v-text-field
								v-model="searchQuery"
								label="Search by name"
								prepend-inner-icon="mdi-magnify"
								hide-details
								@keyup.enter="handleSearch"
							/>
							<v-btn
								color="secondary"
								variant="flat"
								height="48"
								prepend-icon="mdi-refresh"
								@click="handleSearch"
							>
								Refresh
							</v-btn>
							<v-btn
								color="primary"
								variant="flat"
								height="48"
								prepend-icon="mdi-plus"
								@click="openEditModal('create')"
							>
								New Test
							</v-btn>
						</div>
						<v-data-table-server
							:headers="tableHeaders"
							:items="tests"
							v-model:items-per-page="itemsPerPage"
							:items-length="totalTests"
							:loading="loading"
							:search="searchQuery"
							item-value="uuid"
							@update:options="loadTests"
						>
							<template v-slot:item.name="{ item }">
								<div class="d-flex align-center ga-2">
									<v-icon
										v-if="item.is_locked"
										icon="mdi-lock"
										v-tooltip="`Test is in use and can't be edited`"
									/>
									<span class="text-truncate">
										{{ item.name || "Unknown" }}
									</span>
								</div>
							</template>

							<!-- Variant Count -->
							<template v-slot:item.variants.length="{ item }">
								<v-chip size="small" class="ml-1" variant="flat">
									{{ item.variants.length }}
								</v-chip>
							</template>

							<!-- Description -->
							<template v-slot:item.description="{ item }">
								<span class="text-truncate">{{
									item.description || "No description"
								}}</span>
							</template>

							<!-- Actions -->
							<template v-slot:item.actions="{ item }">
								<div class="d-flex justify-end ga-0.5">
									<v-btn
										v-tooltip="'Edit Variants'"
										variant="text"
										size="32"
										color="primary"
										@click="viewTestDetails(item)"
									>
										<v-icon icon="fa-solid fa-sliders" />
									</v-btn>
									<v-divider vertical class="mx-1" />
									<v-btn
										v-tooltip="'Edit Test'"
										variant="text"
										density="comfortable"
										size="32"
										:disabled="item.is_locked"
										@click="openEditModal('edit', item)"
									>
										<v-icon icon="fa-solid fa-pen" size="small" />
									</v-btn>
									<v-btn
										v-tooltip="'Delete Test'"
										variant="text"
										size="32"
										color="error"
										:disabled="item.is_locked"
										@click="deleteTest(item)"
									>
										<v-icon icon="fa-solid fa-trash" />
									</v-btn>
								</div>
							</template>

							<!-- Empty State -->
							<template v-slot:no-data>
								<div class="d-flex flex-column align-center pa-6">
									<v-icon
										icon="fa-solid fa-flask"
										size="large"
										class="mb-3 text-grey"
									/>
									<p class="text-subtitle-1 text-grey">No AB tests found</p>
									<v-btn
										color="primary"
										class="mt-3"
										@click="openEditModal('create')"
									>
										Create test
									</v-btn>
								</div>
							</template>
						</v-data-table-server>
					</div>
				</v-card>
			</v-col>
		</v-row>
	</section>

	<!-- Modals -->
	<!-- Create/Edit Modal -->
	<ModalEditABTest
		v-model:visible="showEditModal"
		v-model:model="selectedTest"
		:mode="editMode"
		@create="createTest"
		@update="updateTest"
	/>

	<!-- View Details Modal -->
	<Modal
		:title="editableTest?.name || 'Test Details'"
		icon="fa-solid fa-flask"
		v-model:visible="showTestDetailsModal"
		width="80rem"
	>
		<div v-if="editableTest">
			<ABTestItem v-model="editableTest" v-model:valid="isValidTest" />
		</div>
		<template #actions>
			<v-btn color="error" variant="text" @click="closeTestDetails">
				Close
			</v-btn>

			<v-btn
				color="primary"
				:loading="loading"
				variant="flat"
				:disabled="!isValidTest || (editableTest && editableTest.is_locked)"
				@click="saveTestChanges"
			>
				Save
			</v-btn>
		</template>
	</Modal>
</template>

<style scoped lang="scss">
.ab-testing-dashboard {
	.text-truncate {
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
		max-width: 300px;
		display: inline-block;
	}
}
</style>
